import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, User, AlertCircle, Eye, EyeOff, RefreshCw, CalendarDays } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { EmailVerificationPrompt } from '@/components/EmailVerificationPrompt';
import { useEmailVerification } from '@/hooks/useEmailVerification';
import APP_VERSION from '@/config/version';
import { SEO } from '@/components/SEO';

/** Minimum age to hold a LuvALL account. */
const MIN_AGE = 13;
/** Below this age a parent/guardian confirmation is required. */
const GUARDIAN_AGE = 16;

/** Whole years between a YYYY-MM-DD date and today, or null when implausible. */
const calculateAge = (isoDate: string): number | null => {
  if (!isoDate) return null;
  const birth = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  if (birth > today) return null;
  if (birth.getFullYear() < 1900) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
};

const Auth = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [dob, setDob] = useState('');
  const [guardianConfirmed, setGuardianConfirmed] = useState(false);
  const dobAge = calculateAge(dob);
  const needsGuardianConsent = dobAge !== null && dobAge >= MIN_AGE && dobAge < GUARDIAN_AGE;

  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
    }
  }, [location.search]);

  const { user, signIn, signUp } = useAuth();
  const { resendVerification, isResending, resendCooldown } = useEmailVerification();

  const getReturnUrl = () => {
    try {
      const storedReturnUrl = sessionStorage.getItem('auth_return_url');
      if (storedReturnUrl && storedReturnUrl !== '/auth' && storedReturnUrl !== 'undefined' && storedReturnUrl.trim() !== '') {
        if (storedReturnUrl.startsWith('/') && !storedReturnUrl.includes('undefined')) {
          return storedReturnUrl;
        } else {
          sessionStorage.removeItem('auth_return_url');
        }
      }
      const fromState = location.state?.from;
      if (fromState?.pathname && fromState.pathname !== '/auth') {
        const searchParams = fromState.search || '';
        const fullPath = fromState.pathname + searchParams;
        if (!fullPath.includes('undefined')) {
          return fullPath;
        }
      }
      return '/';
    } catch (error) {
      try { sessionStorage.removeItem('auth_return_url'); } catch (e) {}
      return '/';
    }
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem('pending_verification_email');
    if (storedEmail) {
      setPendingVerificationEmail(storedEmail);
      setNeedsVerification(true);
      setEmail(storedEmail);
    }
  }, []);

  useEffect(() => {
    if (user) {
      const returnUrl = getReturnUrl();
      if (returnUrl && returnUrl !== '/auth' && !returnUrl.includes('undefined')) {
        try {
          sessionStorage.removeItem('auth_return_url');
          localStorage.removeItem('pending_verification_email');
        } catch (error) {}
        navigate(returnUrl, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isLogin) {
      if (!dob) {
        setError('Please enter your date of birth.');
        return;
      }
      if (dobAge === null) {
        setError('Please enter a valid date of birth.');
        return;
      }
      if (dobAge < MIN_AGE) {
        setError(`You must be at least ${MIN_AGE} years old to use LuvALL.`);
        return;
      }
      if (dobAge < GUARDIAN_AGE && !guardianConfirmed) {
        setError('Please confirm you have your parent or guardian\'s permission to use LuvALL.');
        return;
      }
    }

    setLoading(true);
    setNeedsVerification(false);

    try {
      if (isLogin) {
        await signIn(email, password);
      } else {
        const ageConfirmedAt = new Date().toISOString();
        await signUp(email, password, fullName, ageConfirmedAt, dob);
        try {
          localStorage.setItem('luvall-age-confirmed-at', ageConfirmedAt);
          localStorage.setItem('luvall-dob', dob);
        } catch {}
        setPendingVerificationEmail(email);
        setShowVerificationPrompt(true);
      }

    } catch (err: any) {
      if (err.message === 'Email not confirmed') {
        localStorage.setItem('pending_verification_email', email);
        setPendingVerificationEmail(email);
        setNeedsVerification(true);
        setError(t('auth.emailNotConfirmed'));
      } else {
        setError(err.message || 'An error occurred during authentication');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingVerificationEmail) return;
    setError('');
    try {
      await resendVerification(pendingVerificationEmail);
      setShowVerificationPrompt(true);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setFullName('');
    setError('');
    setShowPassword(false);
    // GDPR: never carry an age declaration across form resets/mode switches
    setDob('');
    setGuardianConfirmed(false);
    setShowVerificationPrompt(false);
    setPendingVerificationEmail('');
    setNeedsVerification(false);
    localStorage.removeItem('pending_verification_email');
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    resetForm();
  };

  const handleBackToSignup = () => {
    setShowVerificationPrompt(false);
    setPendingVerificationEmail('');
    setIsLogin(false);
    localStorage.removeItem('pending_verification_email');
  };

  if (showVerificationPrompt && pendingVerificationEmail) {
    return (
      <div
        className="min-h-[100dvh] flex items-start sm:items-center justify-center overflow-y-auto p-4 bg-gradient-to-br from-primary/10 via-background to-primary/5"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)',
        }}
      >
        <div className="w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 mb-4">
              <img src="/luvall-logo.png" alt="LuvALL Logo" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">LuvALL</h1>
            <p className="text-muted-foreground">{t('auth.verifyEmail')}</p>
          </div>

          <EmailVerificationPrompt
            email={pendingVerificationEmail}
            onBackToSignup={handleBackToSignup}
          />

          <div className="text-center mt-6 space-y-1">
            <p className="text-sm text-muted-foreground">{t('auth.copyright', { year: new Date().getFullYear() })}</p>
            <p className="text-xs text-muted-foreground/70">{APP_VERSION.display}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-[100dvh] flex items-start sm:items-center justify-center overflow-y-auto p-4 bg-gradient-to-br from-primary/10 via-background to-primary/5"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)',
      }}
    >
      <SEO
        title="Sign In"
        description="Sign in or create your free LuvALL account to track padel matches, join groups, and improve your game."
        canonical="/auth"
      />
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 mb-3 sm:mb-4">
            <img src="/luvall-logo.png" alt="LuvALL Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">LuvALL — Sign in to your padel community</h1>
          <p className="text-sm sm:text-base text-muted-foreground">{t('auth.joinCommunity')}</p>
        </div>


        <Card className="shadow-2xl border-0 bg-card/95 backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl text-center font-bold text-foreground">
              {isLogin ? t('auth.welcomeBack') : t('auth.joinLuvALL')}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {isLogin ? t('auth.signInToTrack') : t('auth.createFreeAccount')}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0 pb-6">
            {needsVerification && pendingVerificationEmail && (
              <Alert className="mb-4 border-blue-200 bg-blue-50">
                <Mail className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-700">
                  <div className="space-y-2">
                    <p>{t('auth.emailNeedsVerification')}</p>
                    <Button
                      onClick={handleResendVerification}
                      disabled={isResending || resendCooldown > 0}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isResending ? (
                        <>
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          {t('auth.sending')}
                        </>
                      ) : resendCooldown > 0 ? (
                        t('auth.resendIn', { seconds: resendCooldown })
                      ) : (
                        t('auth.resendVerification')
                      )}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert className="mb-4 border-red-200 bg-red-50" variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium text-foreground">{t('auth.fullName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input 
                      id="fullName" 
                      type="text" 
                      placeholder={t('auth.enterFullName')} 
                      value={fullName} 
                      onChange={e => setFullName(e.target.value)} 
                      className="pl-10 border-border focus:border-primary focus:ring-primary" 
                      required={!isLogin} 
                      disabled={loading} 
                    />
                  </div>
                </div>
              )}

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="dob" className="text-sm font-medium text-foreground">Date of birth</Label>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="dob"
                      type="date"
                      value={dob}
                      onChange={e => setDob(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      min="1900-01-01"
                      className="pl-10 border-border focus:border-primary focus:ring-primary"
                      required={!isLogin}
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We use this to confirm you meet the minimum age for LuvALL. Your date of birth is never
                    shown to other players.
                  </p>
                </div>
              )}

              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder={t('auth.enterEmail')} 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    className="pl-10 border-border focus:border-primary focus:ring-primary" 
                    required 
                    disabled={loading} 
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">{t('auth.password')}</Label>
                  {isLogin && (
                    <Link to="/forgot-password" className="text-sm text-primary hover:text-primary/80 font-medium hover:underline">
                      {t('auth.forgotPassword')}
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="password" 
                    type={showPassword ? "text" : "password"} 
                    placeholder={t('auth.enterPassword')} 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    className="pl-10 pr-10 border-border focus:border-primary focus:ring-primary" 
                    required 
                    disabled={loading} 
                    minLength={6}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground focus:outline-none" 
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {!isLogin && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('auth.passwordMinLength')}
                  </p>
                )}
              </div>

              {!isLogin && (
                <div className="space-y-3">
                  {needsGuardianConsent && (
                    <label
                      htmlFor="guardian-confirm"
                      className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3 cursor-pointer min-h-[44px]"
                    >
                      <input
                        id="guardian-confirm"
                        type="checkbox"
                        checked={guardianConfirmed}
                        onChange={(e) => setGuardianConfirmed(e.target.checked)}
                        disabled={loading}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      />
                      <span className="text-xs text-muted-foreground">
                        My parent or guardian has given me permission to use LuvALL.
                      </span>
                    </label>
                  )}


                  <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
                    <p className="font-medium text-foreground">Your profile will be public by default.</p>
                    <p>
                      Other signed-in players will see your name, photo, skill rating and the matches and
                      rankings you take part in. Your email, gender, postal code and exact location stay
                      private. You can switch your profile to private any time in{' '}
                      <span className="text-foreground">Settings → Privacy</span>.
                    </p>
                  </div>
                </div>
              )}
              

              
              <Button 
                type="submit" 
                className="w-full bg-padel-green-light hover:bg-padel-green-dark text-white font-medium py-2.5 transition-colors duration-200" 
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLogin ? t('auth.signIn') : t('auth.createAccount')}
              </Button>
            </form>
            
            <div className="mt-6">
              <Separator className="bg-border" />
              <div className="mt-4 text-center text-sm">
                <span className="text-muted-foreground">
                  {isLogin ? t('auth.noAccount') : t('auth.haveAccount')}
                </span>
                {' '}
                <Button 
                  variant="link" 
                  onClick={toggleMode} 
                  className="p-0 h-auto text-primary hover:text-primary/80 font-medium" 
                  disabled={loading}
                >
                  {isLogin ? t('auth.signUp') : t('auth.signIn')}
                </Button>
              </div>

              <div className="mt-3 text-center">
                <Link
                  to="/what-you-can-do"
                  className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary hover:underline"
                >
                  See what you can do in LuvALL first
                </Link>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-xs text-muted-foreground">
                <Link to="/terms" className="hover:text-primary transition-colors duration-200 hover:underline">
                  {t('auth.termsOfService')}
                </Link>
                <span className="hidden sm:inline text-border">•</span>
                <Link to="/privacy" className="hover:text-primary transition-colors duration-200 hover:underline">
                  {t('auth.privacyPolicy')}
                </Link>
              </div>
              <p className="text-center text-xs text-muted-foreground/70 mt-2">
                {t('auth.termsAgreement')}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6 space-y-1">
          <p className="text-sm text-muted-foreground">{t('auth.copyright', { year: new Date().getFullYear() })}</p>
          <p className="text-xs text-muted-foreground/70">{APP_VERSION.display}</p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
